import { useEffect, useRef, useState, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import {
  PIShell,
  Drill,
  PIInsight,
  PINoteImage,
  PracticeLadder,
} from "@/components/pi/PIShell";
import { PianoDiagram, KeyHighlight } from "@/components/PianoDiagram";
import { LessonMidi } from "@/components/MidiPlayer";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio, playSequence } from "@/audio/piano";

/**
 * Piano Intermediate · Lesson 22 — Minor Chord Shapes on the Circle of Fifths.
 *
 * Same Circle-of-Fifths logic as Lesson 13, but for minor triads. Three
 * shape families (WBW / WWW / BWB), three "outliers" (B♭m / Bm / E♭m).
 * KEY pedagogical move: minor = major with the third dropped a semitone.
 * That single rule lets you derive any minor chord from its major sibling.
 *
 * Bespoke visual: the Major↔Minor Bridge — a side-by-side chord-shape
 * diff. Pick a chord; see the major triad on the left, the minor triad
 * on the right, with a glowing arrow showing the third note dropping a
 * semitone. The student SEES the rule, not just hears about it.
 */

interface MinorChord {
  letter: string;
  pc: number;
  /** Pattern of natural minor triad keys. */
  pattern: "WBW" | "WWW" | "BWB" | "WWB" | "BBW" | "BBB";
  family: 1 | 2 | 3 | 4;
  cofPos: number;
}

// Every row below is the ACTUAL key colour of root / ♭3 / 5, checked against
// the keyboard: three families of three (WBW, WWW, BWB) plus three outliers,
// which is exactly what FAMILY_INFO already said.
const MINOR_CHORDS: MinorChord[] = [
  { letter: "Cm",  pc: 0,  pattern: "WBW", family: 1, cofPos: 0 },  // C Eb G — W B W
  { letter: "Gm",  pc: 7,  pattern: "WBW", family: 1, cofPos: 1 },  // G Bb D — W B W
  { letter: "Dm",  pc: 2,  pattern: "WWW", family: 2, cofPos: 2 },  // D F A — W W W
  { letter: "Am",  pc: 9,  pattern: "WWW", family: 2, cofPos: 3 },  // A C E — W W W
  { letter: "Em",  pc: 4,  pattern: "WWW", family: 2, cofPos: 4 },  // E G B — W W W
  { letter: "Bm",  pc: 11, pattern: "WWB", family: 4, cofPos: 5 },  // B D F♯ — W W B (outlier)
  { letter: "F#m", pc: 6,  pattern: "BWB", family: 3, cofPos: 6 },  // F# A C# — B W B
  { letter: "C#m", pc: 1,  pattern: "BWB", family: 3, cofPos: 7 },  // C# E G# — B W B
  { letter: "G#m", pc: 8,  pattern: "BWB", family: 3, cofPos: 8 },  // G# B D# — B W B (Jason calls these G# not Ab in minor)
  { letter: "Ebm", pc: 3,  pattern: "BBB", family: 4, cofPos: 9 },  // E♭ G♭ B♭ — all three black (outlier)
  { letter: "Bbm", pc: 10, pattern: "BBW", family: 4, cofPos: 10 }, // B♭ D♭ F — B B W (outlier)
  { letter: "Fm",  pc: 5,  pattern: "WBW", family: 1, cofPos: 11 }, // F Ab C — W B W
];

const FAMILY_INFO: Record<1 | 2 | 3 | 4, { label: string; description: string; chordList: string }> = {
  1: { label: "WBW", description: "White-Black-White: a black third sandwiched between two white outers.", chordList: "Cm · Fm · Gm" },
  2: { label: "WWW", description: "All white — the three minor chords of C major (ii, iii and vi).", chordList: "Dm · Am · Em" },
  3: { label: "BWB", description: "Black-White-Black: the dark mirror of the major BWB family.", chordList: "F♯m · C♯m · G♯m" },
  4: { label: "Outliers", description: "The remaining three shapes don't share a family. Memorise each on its own terms.", chordList: "Bm (W-W-B) · B♭m (B-B-W) · E♭m (B-B-B)" },
};

export default function PianoIntermediate22() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "22");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="C" availableQualities={["natural-minor"]}>
        <PIShell
          title="Minor Chord Shapes — the Circle of Fifths Way"
          exerciseGoal="Twelve minor triads. Three families. Three outliers. The single rule that makes any minor chord findable: drop the major third by a semitone."
          bpm={92}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);
  const [chordIdx, setChordIdx] = useState(0);
  const chord = MINOR_CHORDS[chordIdx];

  // Master keyboard: light up the minor triad's three voices
  const masterHighlights: KeyHighlight[] = [
    { pc: chord.pc,                tone: "root",  label: "1" },
    { pc: (chord.pc + 3) % 12,     tone: "voice", label: "♭3" },
    { pc: (chord.pc + 7) % 12,     tone: "voice", label: "5" },
  ];

  const playMajor = async () => {
    kickAudio();
    const root = tonicMidi + ((chord.pc - k.tonicPc) % 12 + 12) % 12;
    await playSequence(
      [{
        time: 0,
        notes: [
          midiToPitchName(root, k.useFlats),
          midiToPitchName(root + 4, k.useFlats),
          midiToPitchName(root + 7, k.useFlats),
        ],
        duration: "2n",
      }],
      92
    );
  };
  const playMinor = async () => {
    kickAudio();
    const root = tonicMidi + ((chord.pc - k.tonicPc) % 12 + 12) % 12;
    await playSequence(
      [{
        time: 0,
        notes: [
          midiToPitchName(root, k.useFlats),
          midiToPitchName(root + 3, k.useFlats),
          midiToPitchName(root + 7, k.useFlats),
        ],
        duration: "2n",
      }],
      92
    );
  };

  const family = FAMILY_INFO[chord.family];

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <PianoDiagram
          highlights={masterHighlights}
          octaves={2}
          startOctave={3}
          playable
          ariaLabel={`${chord.letter} on the keyboard`}
        />
        <p className="mt-2 text-[12.5px] text-cyan-300/70">
          Master keyboard reflects the active chord. Click chords on the
          ring below to switch.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The big idea" title="Minor = major with the third dropped">
          <p>
            If you know your major triads, you already half-know your
            minor triads. The rule is one sentence:{" "}
            <strong>drop the major-third (the middle note) down by a
            semitone</strong>. Everything else stays. Same root. Same
            fifth. The third is what changes mood.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "If you want to form a minor chord given a major chord, it's not
            too big a deal. You take a major, A major in this case, you drop
            the middle note or the third, which is the major third, down a
            step."
          </PIQuote>
          <MajorMinorBridge chord={chord} onPlayMajor={playMajor} onPlayMinor={playMinor} />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The wheel" title="Twelve minor chords, four shape families">
          <p>
            Same Circle of Fifths layout as Lesson 21, but the family
            colourings shift because the third has moved. Click any chord
            to set it active. The shape pattern (W/B per finger) shows
            beneath the wheel.
          </p>
          <MinorWheelControls chords={MINOR_CHORDS} chordIdx={chordIdx} setChordIdx={setChordIdx} />
          <MinorShapeWheel
            chords={MINOR_CHORDS}
            activeIdx={chordIdx}
            onPick={setChordIdx}
          />
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 p-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
                Active chord
              </div>
              <div className="mt-1 text-2xl font-bold text-amber-100">
                {chord.letter}
              </div>
              <div className="mt-1 font-mono text-[12px] text-amber-200/90">
                Pattern: <strong>{chord.pattern}</strong>
              </div>
            </div>
            <div className="rounded-xl border border-cyan-400/30 bg-cyan-950/40 p-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-300">
                Shape family
              </div>
              <div className="mt-1 text-[15px] font-bold text-cyan-100">
                {family.label}
              </div>
              <div className="mt-1 text-[12px] leading-snug text-cyan-200/85">
                {family.description}
              </div>
              <div className="mt-1 font-mono text-[12px] text-cyan-200/90">
                Members: {family.chordList}
              </div>
            </div>
          </div>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="Naming convention" title="Why we say G♯m, not A♭m">
          <p>
            One subtle rule that catches students out: Jason names the
            black-white-black minor family with <strong>sharps</strong> —
            F♯m, C♯m, <em>G♯m</em>, not <em>A♭m</em>. Why? Because those
            minor chords come from sharp-key signatures (E major, A major,
            B major all have G♯ in them), so the sharp name keeps the
            relationship clear.
          </p>
          <p>
            The black-white-black MAJOR family, on the other hand, is
            named with flats — A♭ major, D♭ major, E♭ major — because those
            chords belong to the flat keys (F, B♭, E♭, A♭ and D♭ major).
          </p>
          <PIInsight>
            <p>
              <strong>So you say G♯ minor, but A♭ major.</strong> Jason's point:
              that's how you communicate with other musicians.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Three minor outliers" title="Bm, B♭m, E♭m don't fit a family">
          <p>
            <strong>Bm</strong> = B-D-F♯ (W-W-B). <strong>B♭m</strong> =
            B♭-D♭-F (B-B-W). <strong>E♭m</strong> = E♭-G♭-B♭ (B-B-B). Each
            is a one-off shape. The good news: there are only three of
            them, and the B-rooted family follows the same oddness
            principle Jason called out in the major lesson — "B's are
            unique."
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="5" label="The inversions" title="Each shape has three">
          <p>
            Once you know the root-position shape, the two inversions
            follow easily — but the SHAPE PATTERN changes. A WBW root
            position becomes BWW first inversion (because the order of
            three keys rotates), and WWB second inversion. So every shape
            really comes in <em>three positions</em> — root, first and second
            inversion. Today we only learn the root positions.
            Inversions get their own dedicated practice in later lessons.
          </p>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="6" label="Play along" title="All 12 minor chords, walked through CoF">
          <LessonMidi
            courseSlug="piano-intermediate"
            order="22"
            label="All 12 minor chord shapes"
            originalBpm={92}
          />
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="7" label="Practice ladder" title="Twelve minutes, four families">
          <PracticeLadder
            intro={
              <>
                Same family-by-family approach as the major lesson. Pair
                this practice with a quick re-run of Lesson 21's major
                chords so the major→minor connection stays alive.
              </>
            }
            rungs={[
              { title: "WBW · Cm, Fm, Gm",        minutes: 3, accent: "amber",   detail: "White-black-white triads. The black middle note is your visual anchor — once you see it, the hand drops into shape." },
              { title: "WWW · Dm, Am, Em",        minutes: 3, accent: "cyan",    detail: "All-white. The natural minor triads of D, A, E majors' relative-minor partners. Easiest of the four families." },
              { title: "BWB · F♯m, C♯m, G♯m",     minutes: 3, accent: "rose",    detail: "Black-white-black. The hand fits like a glove because the black keys cradle the fingers." },
              { title: "Outliers · Bm, B♭m, E♭m", minutes: 3, accent: "violet",  detail: "Three rebel chords. Memorise each by its individual pattern. Then walk all 12 minor chords clockwise around the wheel." },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: Major↔Minor Bridge — chord shape diff with arrow ── */
function MajorMinorBridge({
  chord,
  onPlayMajor,
  onPlayMinor,
}: {
  chord: MinorChord;
  onPlayMajor: () => void;
  onPlayMinor: () => void;
}) {
  const SHARP = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
  const root = SHARP[chord.pc];
  const majThird = SHARP[(chord.pc + 4) % 12];
  const minThird = SHARP[(chord.pc + 3) % 12];
  const fifth = SHARP[(chord.pc + 7) % 12];

  return (
    <div className="my-4 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_1fr]">
      {/* Major side */}
      <div className="rounded-2xl border border-amber-400/30 bg-amber-500/5 p-3">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
          {chord.letter.replace(/m$/, "")} major
        </div>
        <div className="space-y-1 text-[14px] font-mono text-amber-100">
          <Voice label="5th" name={fifth} highlight />
          <Voice label="3rd" name={majThird} accent="amber" big />
          <Voice label="root" name={root} highlight />
        </div>
        <button
          onClick={onPlayMajor}
          className="mt-2 w-full rounded-lg bg-amber-400 px-3 py-1.5 text-[12px] font-bold text-zinc-900 hover:bg-amber-300"
        >
          ▶ Hear major
        </button>
      </div>

      {/* Arrow */}
      <div className="flex flex-col items-center justify-center">
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }}
          className="flex flex-col items-center"
        >
          <span className="text-[11px] font-bold uppercase tracking-wider text-cyan-300">
            drop the 3rd
          </span>
          <span className="text-3xl text-amber-300">↓</span>
          <span className="font-mono text-[11px] text-cyan-300">
            −1 semitone
          </span>
        </motion.div>
      </div>

      {/* Minor side */}
      <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/10 p-3">
        <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-300">
          {chord.letter}
        </div>
        <div className="space-y-1 text-[14px] font-mono text-cyan-100">
          <Voice label="5th" name={fifth} highlight />
          <Voice label="♭3" name={minThird} accent="cyan" big />
          <Voice label="root" name={root} highlight />
        </div>
        <button
          onClick={onPlayMinor}
          className="mt-2 w-full rounded-lg bg-cyan-400 px-3 py-1.5 text-[12px] font-bold text-zinc-900 hover:bg-cyan-300"
        >
          ▶ Hear minor
        </button>
      </div>
    </div>
  );
}

function Voice({
  label,
  name,
  highlight,
  accent,
  big,
}: {
  label: string;
  name: string;
  highlight?: boolean;
  accent?: "amber" | "cyan";
  big?: boolean;
}) {
  const cls = accent === "amber"
    ? "bg-amber-400 text-zinc-900"
    : accent === "cyan"
      ? "bg-cyan-400 text-zinc-900"
      : "bg-zinc-800 text-cyan-200";
  return (
    <div className="flex items-center gap-2">
      <span className="w-10 text-[10px] uppercase tracking-wider text-zinc-400">{label}</span>
      <span className={`flex-1 rounded px-2 py-1 ${cls} ${big ? "text-base font-bold" : ""} ${highlight ? "" : ""}`}>
        {name}
      </span>
    </div>
  );
}

/* ── Walk + family filter for minor wheel ── */
function MinorWheelControls({
  chords,
  chordIdx,
  setChordIdx,
}: {
  chords: MinorChord[];
  chordIdx: number;
  setChordIdx: (i: number) => void;
}) {
  const [walking, setWalking] = useState<"cw" | "ccw" | null>(null);
  const [familyFilter, setFamilyFilter] = useState<1 | 2 | 3 | 4 | null>(null);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!walking) return;
    intervalRef.current = window.setInterval(() => {
      const cur = chords[chordIdx];
      const target = walking === "cw" ? (cur.cofPos + 1) % 12 : (cur.cofPos + 11) % 12;
      const next = chords.findIndex((c) => c.cofPos === target);
      if (next >= 0) setChordIdx(next);
    }, 1200);
    return () => {
      if (intervalRef.current != null) window.clearInterval(intervalRef.current);
    };
  }, [walking, chordIdx, chords, setChordIdx]);

  const jumpToFamily = (fam: 1 | 2 | 3 | 4) => {
    const cur = chords[chordIdx];
    if (cur.family === fam) {
      const family = chords.filter((c) => c.family === fam);
      const i = family.findIndex((c) => c.letter === cur.letter);
      const next = family[(i + 1) % family.length];
      setChordIdx(chords.findIndex((c) => c.letter === next.letter));
    } else {
      const idx = chords.findIndex((c) => c.family === fam);
      if (idx >= 0) setChordIdx(idx);
    }
    setFamilyFilter(fam);
    setWalking(null);
  };

  const familyLabels: Record<1 | 2 | 3 | 4, string> = { 1: "WBW", 2: "WWW", 3: "BWB", 4: "Outliers" };
  const familyColors: Record<1 | 2 | 3 | 4, string> = {
    1: "bg-cyan-500/30 text-cyan-200 border-cyan-400/60",
    2: "bg-amber-500/30 text-amber-200 border-amber-400/60",
    3: "bg-rose-500/30 text-rose-200 border-rose-400/60",
    4: "bg-violet-500/30 text-violet-200 border-violet-400/60",
  };

  return (
    <div className="my-3 flex flex-wrap items-center gap-2">
      <button
        onClick={() => setWalking((w) => (w === "ccw" ? null : "ccw"))}
        className={`rounded-md border px-3 py-1.5 text-[12px] font-bold ${
          walking === "ccw" ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-cyan-400/40 bg-cyan-500/5 text-cyan-200 hover:bg-cyan-500/15"
        }`}
      >
        ⟲ Walk CCW
      </button>
      <button
        onClick={() => setWalking((w) => (w === "cw" ? null : "cw"))}
        className={`rounded-md border px-3 py-1.5 text-[12px] font-bold ${
          walking === "cw" ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-cyan-400/40 bg-cyan-500/5 text-cyan-200 hover:bg-cyan-500/15"
        }`}
      >
        Walk CW ⟳
      </button>
      <span className="ml-2 text-[11px] uppercase tracking-wider text-cyan-300/70">Filter:</span>
      {([1, 2, 3, 4] as const).map((fam) => {
        const active = familyFilter === fam;
        return (
          <button
            key={fam}
            onClick={() => jumpToFamily(fam)}
            className={`rounded-md border px-2 py-1 font-mono text-[11px] font-bold ${
              active ? familyColors[fam] : "border-cyan-400/30 bg-zinc-900/40 text-cyan-300/80 hover:bg-cyan-500/10"
            }`}
          >
            {familyLabels[fam]}
          </button>
        );
      })}
    </div>
  );
}

/* ── ShapeWheel for minor — colour-coded by family ── */
function MinorShapeWheel({
  chords,
  activeIdx,
  onPick,
}: {
  chords: MinorChord[];
  activeIdx: number;
  onPick: (i: number) => void;
}) {
  const cx = 200;
  const cy = 200;
  const r = 140;
  const familyColors: Record<1 | 2 | 3 | 4, string> = {
    1: "#22D3EE", // cyan for WBW (Cm/Fm/Gm)
    2: "#F59E0B", // amber for WWW (Dm/Am/Em)
    3: "#FB7185", // rose for BWB (F#m/C#m/G#m)
    4: "#A78BFA", // violet for outliers
  };
  return (
    <div className="my-4 rounded-2xl border border-cyan-400/30 bg-zinc-900/40 p-4">
      <svg viewBox="0 0 400 400" className="w-full">
        <circle cx={cx} cy={cy} r={r + 18} fill="none" stroke="#475569" strokeDasharray="2 4" strokeWidth="1" />
        {chords.map((c, i) => {
          const angle = (c.cofPos * 30 - 90) * (Math.PI / 180);
          const x = cx + r * Math.cos(angle);
          const y = cy + r * Math.sin(angle);
          const isActive = i === activeIdx;
          return (
            <g key={c.letter} onClick={() => onPick(i)} style={{ cursor: "pointer" }}>
              <motion.circle
                cx={x}
                cy={y}
                animate={{ r: isActive ? 30 : 24 }}
                transition={{ duration: 0.18 }}
                fill={familyColors[c.family]}
                stroke={isActive ? "#FFF" : "#0F172A"}
                strokeWidth={isActive ? 3 : 2}
                opacity={isActive ? 1 : 0.85}
              />
              <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle" fontFamily="Inter,system-ui" fontSize={isActive ? "15" : "13"} fontWeight="700" fill="#0F172A" style={{ pointerEvents: "none" }}>
                {c.letter}
              </text>
              <text x={x} y={y + 32} textAnchor="middle" dominantBaseline="middle" fontFamily="JetBrains Mono,monospace" fontSize="9" fill={familyColors[c.family]} opacity="0.85" style={{ pointerEvents: "none" }}>
                {c.pattern}
              </text>
            </g>
          );
        })}
        <text x={cx} y={cy - 4} textAnchor="middle" fontFamily="Cormorant Garamond,serif" fontSize="20" fontWeight="600" fill="#94A3B8">12 minor</text>
        <text x={cx} y={cy + 18} textAnchor="middle" fontFamily="Cormorant Garamond,serif" fontSize="20" fontWeight="600" fill="#94A3B8">triads</text>
      </svg>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] md:grid-cols-4">
        {(Object.entries(familyColors) as [string, string][]).map(([fam, color]) => {
          const f = FAMILY_INFO[Number(fam) as 1 | 2 | 3 | 4];
          return (
            <div key={fam} className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
              <span className="font-semibold text-cyan-100">{f.label.split(" ")[0]}</span>
              <span className="ml-auto font-mono text-cyan-300/70">{f.chordList}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── shared ── */
function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · master keyboard</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div initial={reduced ? false : { opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, ease: "easeOut" }}>
      {children}
    </motion.div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote className="text-[17px] leading-relaxed text-cyan-50/95" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}>
        {children}
      </blockquote>
      {attribution && <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">— {attribution}</figcaption>}
    </figure>
  );
}
